import React, { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

const TIPS = {
  "/": "Your daily huddle — today's turnovers, revenue, alerts and the search bar for anything.",
  "/calendar": "Every booking and cleaning turnover across all properties in one view.",
  "/contacts": "Guests, landlords, contractors and suppliers — open one to see their full history.",
  "/bookings": "All reservations. Send the guest check-in link from here.",
  "/journeys": "Track each guest's ID, agreement, deposit and balance payment.",
  "/signed-agreements": "Every signed rental agreement as a stamped PDF.",
  "/inbox": "Email, SMS and WhatsApp in one place, with AI draft replies.",
  "/cleaning": "The turnover board — I'm here, room photos and Done.",
  "/damage-reports": "Guest damage, AI drafts, cost estimates and deposit charges.",
  "/turnover-photos": "Check-out photos per property, with AI room-change flags.",
  "/cleaner-quality": "Each cleaner's flag-free score, month by month.",
  "/campaigns": "Email blasts and quiet-month offers to past guests.",
  "/templates": "Reusable messages with merge tags like {{first_name}}.",
  "/properties": "Units, access codes, photo checklist, leases and compliance.",
  "/statements": "Owner profit statements and the approval queue.",
  "/guest-portal": "Set up the guest guidebook and upsell items.",
  "/finance": "Expenses, income and profit by property.",
  "/compliance": "Certificates and documents with expiry alerts.",
  "/reports": "Occupancy, revenue and end-of-month reports.",
  "/team": "Invite staff and cleaners, roles and quality scores.",
  "/training-guides": "How-to guides, quizzes and certificates — start with the menu guide.",
  "/cost-audit": "History of cleaning and bedding cost changes.",
  "/bulk-history": "Log of bulk imports and mass updates.",
  "/stripe-smoke-test": "Check Stripe payments and webhooks end to end.",
  "/settings": "Profile, 2FA, integrations and automations. Replay this tour any time with the ? button next to Log out.",
};

export const restartTour = async () => {
  await api.post("/me/tour", { seen: false });
  window.dispatchEvent(new Event("ns-tour-restart"));
};

export function Spotlight({ steps, onDone, testPrefix = "app-tour" }) {
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);
  const step = steps[i];
  const measure = useCallback(() => {
    const el = step && document.querySelector(step.sel);
    if (!el) { setRect(null); return; }
    el.scrollIntoView({ block: "nearest" });
    setRect(el.getBoundingClientRect());
  }, [step]);
  useLayoutEffect(() => {
    measure();
    const t = setTimeout(measure, 350);
    window.addEventListener("resize", measure);
    return () => { clearTimeout(t); window.removeEventListener("resize", measure); };
  }, [measure]);
  if (!step) return null;
  const W = 320;
  const top = rect ? Math.min(Math.max(12, rect.bottom + 12 > window.innerHeight - 200 ? rect.top - 200 : rect.bottom + 12), window.innerHeight - 200) : 120;
  const left = rect ? (rect.right + 20 + W < window.innerWidth && rect.width < 400 ? rect.right + 20 : Math.min(Math.max(12, rect.left), window.innerWidth - W - 12)) : 320;
  const sideTop = rect && rect.right + 20 + W < window.innerWidth && rect.width < 400 ? Math.min(Math.max(12, rect.top - 8), window.innerHeight - 200) : top;
  return createPortal(
    <div className="fixed inset-0 z-[100]" style={{ pointerEvents: "auto" }} data-tour-root data-testid={testPrefix}>
      {rect && (
        <div className="absolute rounded-xl ring-2 ring-amber-300 pointer-events-none transition-all duration-300"
          style={{ top: rect.top - 4, left: rect.left - 4, width: rect.width + 8, height: rect.height + 8, boxShadow: "0 0 0 9999px rgba(15,23,42,0.6)" }} />
      )}
      {!rect && <div className="absolute inset-0 bg-slate-900/60" />}
      <div className="absolute bg-white rounded-2xl shadow-2xl p-5 transition-all duration-300" style={{ top: sideTop, left, width: W }}
        data-testid={`${testPrefix}-card`}>
        <div className="text-[11px] uppercase tracking-widest text-teal-700" data-testid={`${testPrefix}-counter`}>Step {i + 1} of {steps.length}</div>
        <div className="font-serif text-xl mt-1 text-slate-900" data-testid={`${testPrefix}-title`}>{step.title}</div>
        <p className="text-sm text-slate-600 mt-1" data-testid={`${testPrefix}-tip`}>{step.tip}</p>
        <div className="flex items-center justify-between mt-4">
          <button onClick={onDone} className="text-xs text-slate-500 hover:text-slate-800" data-testid={`${testPrefix}-skip`}>Skip tour</button>
          <div className="flex gap-2">
            {i > 0 && <Button size="sm" variant="outline" className="rounded-full" onClick={() => setI(i - 1)} data-testid={`${testPrefix}-back`}>Back</Button>}
            {i < steps.length - 1
              ? <Button size="sm" className="rounded-full ns-btn-primary" onClick={() => setI(i + 1)} data-testid={`${testPrefix}-next`}>Next</Button>
              : <Button size="sm" className="rounded-full ns-btn-primary" onClick={onDone} data-testid={`${testPrefix}-done`}>Finish</Button>}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export function AppTour({ items }) {
  const [open, setOpen] = useState(false);
  const [run, setRun] = useState(0);
  useEffect(() => {
    api.get("/me/tour").then((r) => { if (!r.data.seen) setOpen(true); }).catch(() => {});
    const h = () => { setRun((n) => n + 1); setOpen(true); };
    window.addEventListener("ns-tour-restart", h);
    return () => window.removeEventListener("ns-tour-restart", h);
  }, []);
  const finish = () => {
    setOpen(false);
    api.post("/me/tour", { seen: true }).catch(() => {}).finally(() => window.dispatchEvent(new Event("ns-main-tour-done")));
  };
  if (!open) return null;
  const steps = items.filter((it) => TIPS[it.to]).map((it) => ({ sel: `[data-testid="${it.testId}"]`, title: it.label, tip: TIPS[it.to] }));
  return <Spotlight key={run} steps={steps} onDone={finish} />;
}

export function RestartTourCard() {
  return (
    <div className="ns-card p-8 mt-6" data-testid="settings-restart-tour">
      <div className="font-serif text-2xl">Menu tour</div>
      <p className="text-sm text-slate-500 mt-1 mb-4">Replay the quick click-through tour of every menu tab you can see.</p>
      <Button className="rounded-full ns-btn-primary" onClick={() => { restartTour(); window.scrollTo(0, 0); }} data-testid="settings-restart-tour-btn">Restart tour</Button>
    </div>
  );
}
